import type { FrameworkAction, FrameworkArgument } from '../framework/framework.d';
import { parsePythonConstantNumericExpression } from './pythonSourceScanUtils';

export const HIGH_SPEED_MOTOR_CLASS_NAME = 'HighSpeedMotor';
export const HIGH_TORQUE_MOTOR_CLASS_NAME = 'HighTorqueMotor';
export const MOTOR_TYPE_HIGH_SPEED = 'MotorType.HIGH_SPEED';
export const MOTOR_TYPE_HIGH_TORQUE = 'MotorType.HIGH_TORQUE';

export const HIGH_SPEED_MOTOR_MAX_RPM = 500;
export const HIGH_TORQUE_MOTOR_MAX_RPM = 90;

export const ROTATIONS_PER_MINUTE = 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE';
const ROTATIONS_PER_SECOND = 'RotationalVelocityUnits.ROTATIONS_PER_SECOND';
const DEGREES_PER_SECOND = 'RotationalVelocityUnits.DEGREES_PER_SECOND';
const RADIANS_PER_SECOND = 'RotationalVelocityUnits.RADIANS_PER_SECOND';
export const METERS_PER_SECOND = 'LinearVelocityUnits.METERS_PER_SECOND';
const CENTIMETERS_PER_SECOND = 'LinearVelocityUnits.CENTIMETERS_PER_SECOND';
const MILLIMETERS_PER_SECOND = 'LinearVelocityUnits.MILLIMETERS_PER_SECOND';
const INCHES_PER_SECOND = 'LinearVelocityUnits.INCHES_PER_SECOND';
const FEET_PER_SECOND = 'LinearVelocityUnits.FEET_PER_SECOND';
export const MEASURED_AT_MOTOR = 'MeasuredAt.MOTOR';
export const MEASURED_AT_MECHANISM_OUTPUT = 'MeasuredAt.MECHANISM_OUTPUT';
const LINEAR_VELOCITY_LIMIT_SIGNIFICANT_DIGITS = 5;
const POSITIVE_NUMBER_TERM_RE = '[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)(?:[eE][+-]?\\d+)?';
const POSITIVE_NUMBER_FRACTION_RE = new RegExp(`^\\s*(${POSITIVE_NUMBER_TERM_RE})\\s*/\\s*(${POSITIVE_NUMBER_TERM_RE})\\s*$`);

interface MotorVelocityLimitOptions {
  gearRatio?: number | null;
  distancePerOutputRotation?: number | null;
}

function parseFiniteVelocity(rawValue: unknown): number | null {
  const rawText = String(rawValue ?? '').trim();
  if (!rawText) return null;
  const value = Number(rawText);
  return Number.isFinite(value) ? value : null;
}

function linearVelocityToMetersPerSecond(value: number, units: string): number | null {
  switch (units) {
    case METERS_PER_SECOND:
      return value;
    case CENTIMETERS_PER_SECOND:
      return value / 100;
    case MILLIMETERS_PER_SECOND:
      return value / 1000;
    case INCHES_PER_SECOND:
      return value * 0.0254;
    case FEET_PER_SECOND:
      return value * 0.3048;
    default:
      return null;
  }
}

function rotationalVelocityToRpm(value: number, units: string): number | null {
  switch (units) {
    case ROTATIONS_PER_MINUTE:
      return value;
    case ROTATIONS_PER_SECOND:
      return value * 60;
    case DEGREES_PER_SECOND:
      return value / 6;
    case RADIANS_PER_SECOND:
      return (value * 60) / (Math.PI * 2);
    default:
      return null;
  }
}

function formatConvertedVelocity(value: number): string {
  const rounded = Number(value.toFixed(6));
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

export function getLinearVelocitySourceChangeValues(
  action: FrameworkAction | null | undefined,
  values: Record<string, string>,
  nextSource: string
): Record<string, string> | null {
  if (!action?.sourceAwareLinearVelocity || !action.arguments?.length) return null;
  if (nextSource !== MEASURED_AT_MOTOR && nextSource !== MEASURED_AT_MECHANISM_OUTPUT) return null;

  const sourceArg = action.arguments.find((arg) => arg.name === 'source' && arg.type === 'MeasuredAt');
  const currentSource = String(values.source || sourceArg?.defaultValue || '').trim();
  if (currentSource === nextSource) return null;

  const targetUnits = nextSource === MEASURED_AT_MOTOR ? ROTATIONS_PER_MINUTE : METERS_PER_SECOND;
  const nextValues: Record<string, string> = { source: nextSource, units: targetUnits };
  const velocityArg = action.arguments.find((arg) => arg.name === 'velocity');
  if (!velocityArg) return nextValues;

  const velocity = parseFiniteVelocity(values.velocity);
  const unitsArg = action.arguments.find((arg) => arg.name === 'units');
  const currentUnits = String(values.units || unitsArg?.defaultValue || '').trim();
  const gearRatio = velocityArg.motorVelocityLimitGearRatio;
  const distancePerOutputRotation = velocityArg.motorVelocityLimitDistancePerOutputRotation;
  if (
    velocity === null ||
    typeof gearRatio !== 'number' ||
    !Number.isFinite(gearRatio) ||
    gearRatio <= 0 ||
    typeof distancePerOutputRotation !== 'number' ||
    !Number.isFinite(distancePerOutputRotation) ||
    distancePerOutputRotation <= 0
  ) {
    return { ...nextValues, velocity: '' };
  }

  let convertedVelocity: number | null = null;
  if (currentSource === MEASURED_AT_MECHANISM_OUTPUT && nextSource === MEASURED_AT_MOTOR) {
    const metersPerSecond = linearVelocityToMetersPerSecond(velocity, currentUnits);
    convertedVelocity = metersPerSecond === null ? null : ((metersPerSecond * gearRatio) / distancePerOutputRotation) * 60;
  } else if (currentSource === MEASURED_AT_MOTOR && nextSource === MEASURED_AT_MECHANISM_OUTPUT) {
    const rpm = rotationalVelocityToRpm(velocity, currentUnits);
    convertedVelocity = rpm === null ? null : (rpm / 60 / gearRatio) * distancePerOutputRotation;
  }

  return { ...nextValues, velocity: convertedVelocity === null ? '' : formatConvertedVelocity(convertedVelocity) };
}

export function getMotorVelocityLimitRpmForClassName(className: string | undefined): number | null {
  if (className === HIGH_SPEED_MOTOR_CLASS_NAME) return HIGH_SPEED_MOTOR_MAX_RPM;
  if (className === HIGH_TORQUE_MOTOR_CLASS_NAME) return HIGH_TORQUE_MOTOR_MAX_RPM;
  return null;
}

export function getMotorVelocityLimitRpmForMotorType(motorType: string | undefined): number | null {
  if (motorType === MOTOR_TYPE_HIGH_SPEED) return HIGH_SPEED_MOTOR_MAX_RPM;
  if (motorType === MOTOR_TYPE_HIGH_TORQUE) return HIGH_TORQUE_MOTOR_MAX_RPM;
  return null;
}

export function parseMotorVelocityGearRatio(rawValue: unknown): number | null {
  const ratio = parsePythonConstantNumericExpression(rawValue);
  return ratio !== null && ratio > 0 ? ratio : null;
}

export function parseMotorVelocityDistancePerOutputRotation(rawValue: unknown): number | null {
  return parseMotorVelocityPositiveNumber(rawValue);
}

function parseMotorVelocityPositiveNumber(rawValue: unknown): number | null {
  if (typeof rawValue === 'number') return Number.isFinite(rawValue) && rawValue > 0 ? rawValue : null;
  const rawText = String(rawValue ?? '').trim();
  if (!rawText) return null;

  const fractionMatch = rawText.match(POSITIVE_NUMBER_FRACTION_RE);
  if (fractionMatch) {
    const numerator = Number(fractionMatch[1]);
    const denominator = Number(fractionMatch[2]);
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) return null;
    const ratio = numerator / denominator;
    return Number.isFinite(ratio) && ratio > 0 ? ratio : null;
  }

  const ratio = Number(rawText);
  return Number.isFinite(ratio) && ratio > 0 ? ratio : null;
}

export function getMechanismOutputLimitRpm(limitRpm: number, gearRatio: number | null | undefined, source: string | undefined): number | null {
  if (gearRatio === undefined) return limitRpm;
  const normalizedSource = source || MEASURED_AT_MECHANISM_OUTPUT;
  if (normalizedSource === MEASURED_AT_MOTOR) return limitRpm;
  if (normalizedSource !== MEASURED_AT_MECHANISM_OUTPUT) return null;
  if (gearRatio === null) return null;
  if (!Number.isFinite(gearRatio) || gearRatio <= 0) return null;
  return limitRpm / gearRatio;
}

export function getRotationalVelocityLimitForUnits(limitRpm: number, units: string | undefined): number | null {
  const normalizedUnits = units || ROTATIONS_PER_MINUTE;
  switch (normalizedUnits) {
    case ROTATIONS_PER_MINUTE:
      return limitRpm;
    case ROTATIONS_PER_SECOND:
      return limitRpm / 60;
    case DEGREES_PER_SECOND:
      return limitRpm * 6;
    case RADIANS_PER_SECOND:
      return (limitRpm * Math.PI * 2) / 60;
    default:
      return null;
  }
}

export function getLinearVelocityLimitForUnits(
  limitRpm: number,
  distancePerOutputRotation: number | null | undefined,
  units: string | undefined
): number | null {
  if (distancePerOutputRotation === undefined || distancePerOutputRotation === null) return null;
  if (!Number.isFinite(distancePerOutputRotation) || distancePerOutputRotation <= 0) return null;
  const normalizedUnits = units || METERS_PER_SECOND;
  const metersPerSecond = (limitRpm * distancePerOutputRotation) / 60;
  switch (normalizedUnits) {
    case METERS_PER_SECOND:
      return metersPerSecond;
    case CENTIMETERS_PER_SECOND:
      return metersPerSecond * 100;
    case MILLIMETERS_PER_SECOND:
      return metersPerSecond * 1000;
    case INCHES_PER_SECOND:
      return metersPerSecond / 0.0254;
    case FEET_PER_SECOND:
      return metersPerSecond / 0.3048;
    default:
      return null;
  }
}

function shiftDecimal(value: number, places: number): number {
  const [coefficient = '0', exponent = '0'] = value.toString().split('e');
  return Number(`${coefficient}e${Number(exponent) + places}`);
}

function getConservativeLinearVelocityLimit(limit: number): number {
  if (!Number.isFinite(limit) || limit <= 0) return limit;
  const magnitude = Math.floor(Math.log10(limit));
  const decimalPlaces = LINEAR_VELOCITY_LIMIT_SIGNIFICANT_DIGITS - magnitude - 1;
  const shiftedLimit = shiftDecimal(limit, decimalPlaces);
  if (!Number.isFinite(shiftedLimit)) return limit;
  return shiftDecimal(Math.trunc(shiftedLimit), -decimalPlaces);
}

export function withMotorVelocityLimitForAction(
  action: FrameworkAction,
  limitRpm: number | null | undefined,
  options: MotorVelocityLimitOptions = {}
): FrameworkAction {
  if (!limitRpm || !action.arguments?.length) return action;
  let changed = false;
  const hasRotationalVelocityUnitsArg = action.arguments.some((arg) => arg.name === 'units' && arg.type === 'RotationalVelocityUnits');
  const hasLinearVelocityUnitsArg = action.arguments.some((arg) => arg.name === 'units' && arg.type === 'LinearVelocityUnits');
  if (!hasRotationalVelocityUnitsArg && !hasLinearVelocityUnitsArg) return action;

  const nextArguments = action.arguments.map((arg) => {
    if (arg.name !== 'velocity') return arg;
    changed = true;
    return {
      ...arg,
      motorVelocityLimitRpm: limitRpm,
      ...(options.gearRatio !== undefined ? { motorVelocityLimitGearRatio: options.gearRatio } : {}),
      ...(options.distancePerOutputRotation !== undefined ? { motorVelocityLimitDistancePerOutputRotation: options.distancePerOutputRotation } : {}),
    };
  });

  return changed ? { ...action, arguments: nextArguments } : action;
}

export function withResolvedMotorVelocityLimits(action: FrameworkAction | null | undefined, values: Record<string, string>): FrameworkAction | null {
  if (!action) return null;
  if (!action.arguments?.length) return action;

  const unitsArg = action.arguments.find(
    (arg) => arg.name === 'units' && (arg.type === 'RotationalVelocityUnits' || arg.type === 'LinearVelocityUnits')
  );
  const sourceArg = action.arguments.find((arg) => arg.name === 'source' && arg.type === 'MeasuredAt');
  const selectedSource = String(values.source || sourceArg?.defaultValue || '').trim() || undefined;
  const sourceAwareLinearVelocity = action.sourceAwareLinearVelocity === true;
  const sourceAwareUnitFamily = sourceAwareLinearVelocity
    ? selectedSource === MEASURED_AT_MOTOR
      ? 'rotational'
      : selectedSource === MEASURED_AT_MECHANISM_OUTPUT
      ? 'linear'
      : undefined
    : undefined;
  const sourceAwareDefaultUnits =
    sourceAwareUnitFamily === 'rotational' ? ROTATIONS_PER_MINUTE : sourceAwareUnitFamily === 'linear' ? METERS_PER_SECOND : undefined;
  const selectedUnits = String(values.units || sourceAwareDefaultUnits || unitsArg?.defaultValue || '').trim() || undefined;
  let changed = false;

  const nextArguments = action.arguments.map((arg) => {
    if (sourceAwareLinearVelocity && arg.name === 'units' && sourceAwareUnitFamily) {
      const type = sourceAwareUnitFamily === 'rotational' ? 'RotationalVelocityUnits' : 'LinearVelocityUnits';
      const defaultValue = sourceAwareDefaultUnits;
      if (arg.type !== type || arg.defaultValue !== defaultValue) changed = true;
      return { ...arg, type, defaultValue };
    }

    const limitRpm = getArgumentMotorVelocityLimitRpm(arg);
    if (!limitRpm) return arg;

    const isLinearVelocityAction = sourceAwareLinearVelocity ? sourceAwareUnitFamily === 'linear' : unitsArg?.type === 'LinearVelocityUnits';
    const effectiveSource = !sourceAwareLinearVelocity && isLinearVelocityAction ? MEASURED_AT_MECHANISM_OUTPUT : selectedSource;
    const effectiveLimitRpm =
      sourceAwareLinearVelocity && sourceAwareUnitFamily === 'rotational'
        ? limitRpm
        : getMechanismOutputLimitRpm(limitRpm, arg.motorVelocityLimitGearRatio, effectiveSource);
    const limit =
      effectiveLimitRpm === null || (sourceAwareLinearVelocity && !sourceAwareUnitFamily)
        ? null
        : isLinearVelocityAction
        ? getLinearVelocityLimitForUnits(effectiveLimitRpm, arg.motorVelocityLimitDistancePerOutputRotation, selectedUnits)
        : getRotationalVelocityLimitForUnits(effectiveLimitRpm, selectedUnits);
    if (limit === null) {
      changed = true;
      const nextArg = { ...arg };
      delete nextArg.min;
      delete nextArg.max;
      return nextArg;
    }

    const resolvedLimit = isLinearVelocityAction ? getConservativeLinearVelocityLimit(limit) : limit;
    changed = true;
    return { ...arg, min: -resolvedLimit, max: resolvedLimit };
  });

  return changed ? { ...action, arguments: nextArguments } : action;
}

function getArgumentMotorVelocityLimitRpm(arg: FrameworkArgument): number | null {
  return typeof arg.motorVelocityLimitRpm === 'number' && Number.isFinite(arg.motorVelocityLimitRpm) ? arg.motorVelocityLimitRpm : null;
}
